// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useEffect, useCallback, useState } from 'react'
import { useAutosave }  from '@/hooks/useAutosave'
import { useAuthStore } from '@/store'
import { LoadingState } from '@/components/feedback/LoadingState'
import type { SaveStatus } from '@/hooks/useAutosave'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyData = Record<string, any>

interface WizardStepShellProps<T extends AnyData> {
  emptyData:    T
  load:         (userId: string) => Promise<T | null>
  save:         (userId: string, data: T) => Promise<void>
  onSaveStatus: (s: SaveStatus) => void
  // V40-P1: saveNow als optioneel derde arg - backward compat met alle bestaande steps
  children:     (data: T, update: (k: keyof T, v: T[keyof T]) => void, saveNow: () => Promise<void>) => React.ReactNode
  // V40B-P1: optionele ref zodat GuidedWizardPage saveNow kan aanroepen vóór navigatie
  saveRef?:     React.MutableRefObject<(() => Promise<void>) | null>
}

export function WizardStepShell<T extends AnyData>({
  emptyData, load, save, onSaveStatus, children, saveRef,
}: WizardStepShellProps<T>) {
  const { userId }  = useAuthStore()
  const [data,  setData]  = useState<T>(emptyData)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!userId) return
    load(userId).then(d => { if (d) setData(d); setReady(true) })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const onSave = useCallback(async (d: T) => {
    if (userId) await save(userId, d)
  }, [userId, save])

  const { status, saveNow } = useAutosave({ data, onSave, enabled: ready })
  useEffect(() => { onSaveStatus(status) }, [status, onSaveStatus])

  // V40B-P1: koppel saveNow aan externe ref zodat GuidedWizardPage kan aanroepen
  useEffect(() => {
    if (saveRef) saveRef.current = saveNow
    return () => { if (saveRef) saveRef.current = null }
  }, [saveRef, saveNow])

  const update = useCallback((k: keyof T, v: T[keyof T]) => {
    setData(prev => ({ ...prev, [k]: v }))
  }, [])

  if (!ready) return <LoadingState message="Laden..." />
  return <>{children(data, update, saveNow)}</>
}
